import React, { useEffect, useState, useRef, useCallback } from 'react';
import baseURL from '../url';
import './Products.css';
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShoppingCart, faHeart, faFire, faClock, faBolt, faEye, faTag } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ProductosLoading from '../ProductosLoading/ProductosLoading';
import { Link as Anchor } from 'react-router-dom';
import moneda from '../moneda';

// ════════════════════════════════════════════════════════════
// HELPERS: Carrito y Favoritos (compartidos por ambas cards)
// ════════════════════════════════════════════════════════════

let _lastCartClick = 0;
let _lastFavClick  = 0;

function agregarAlCarrito(item, e) {
    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    if (now - _lastCartClick < 300) return;
    _lastCartClick = now;

    const stockNum   = Number(item.stock);
    const stockTexto = String(item.stock).toLowerCase();

    if (stockTexto === 'agotado' || stockNum <= 0) {
        toast.error('Producto agotado', { autoClose: 1200 });
        return;
    }

    const cart              = JSON.parse(localStorage.getItem('cart')) || [];
    const existingIdx       = cart.findIndex(c => c.idProducto === item.idProducto);
    const cantidadEnCarrito = existingIdx !== -1 ? cart[existingIdx].cantidad : 0;

    if (!isNaN(stockNum) && stockTexto !== 'disponible' && cantidadEnCarrito + 1 > stockNum) {
        toast.error(`Solo quedan ${stockNum} piezas disponibles`, { autoClose: 1800 });
        return;
    }

    const { precioFinal } = calcularPrecio(item);

    if (existingIdx !== -1) {
        cart[existingIdx].cantidad += 1;
        cart[existingIdx].precio = precioFinal;
    } else {
        cart.push({
            idProducto: item.idProducto,
            cantidad:   1,
            precio:     precioFinal,
            items:      [],
        });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    toast.success('Producto agregado ✅', { autoClose: 500 });
    window.dispatchEvent(new Event('storage'));
}

function toggleFavorito(idProducto, e) {
    e.preventDefault();
    e.stopPropagation();

    const now = Date.now();
    if (now - _lastFavClick < 300) return null;
    _lastFavClick = now;

    const favs    = JSON.parse(localStorage.getItem('favoritos')) || [];
    const idx     = favs.indexOf(idProducto);
    const agregar = idx === -1;

    if (agregar) {
        favs.push(idProducto);
        toast.success('Agregado a favoritos ❤️', { autoClose: 700 });
    } else {
        favs.splice(idx, 1);
        toast.info('Eliminado de favoritos', { autoClose: 700 });
    }

    localStorage.setItem('favoritos', JSON.stringify(favs));
    window.dispatchEvent(new Event('storage'));
    return agregar;
}

SwiperCore.use([Navigation, Pagination, Autoplay]);

function registrarVisto(idProducto) {
    const vistos   = JSON.parse(localStorage.getItem('recienVistos')) || [];
    const filtrado = vistos.filter(id => id !== idProducto);
    filtrado.unshift(idProducto);
    localStorage.setItem('recienVistos', JSON.stringify(filtrado.slice(0, 20)));
}

// ════════════════════════════════════════════════════════════
// FUNCIONES UTILITARIAS
// ════════════════════════════════════════════════════════════

function calcularPrecio(item) {
    const hoy = new Date();

    const dentroDelPeriodo =
        (!item.precioEspecialInicio || hoy >= new Date(item.precioEspecialInicio)) &&
        (!item.precioEspecialFin    || hoy <= new Date(item.precioEspecialFin));

    const tienePrecioEspecial = item.precioEspecial && dentroDelPeriodo;

    let precioFinal = Number(item.precio);

    if (tienePrecioEspecial) {
        if (item.tipoPrecioEspecial === 'porcentaje') {
            precioFinal = precioFinal - (precioFinal * Number(item.precioEspecial) / 100);
        } else {
            precioFinal = Number(item.precioEspecial);
        }
    }

    return { precioFinal, tienePrecioEspecial };
}

const fmt = (n) =>
    `${moneda} ${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

function calcularDescuento(precioOriginal, precioFinal) {
    if (!precioOriginal || precioOriginal <= precioFinal) return null;
    return Math.round((1 - precioFinal / precioOriginal) * 100);
}

function obtenerImagen(item) {
    return item.imagen1 || item.imagen2 || item.imagen3 || item.imagen4 || null;
}

// ════════════════════════════════════════════════════════════
// SUBCOMPONENTE: CatCirculo  ← NUEVO SESIÓN 5
// Categoría con imagen circular y nombre debajo
// ════════════════════════════════════════════════════════════
function CatCirculo({ cat, activa, onClick }) {
    return (
        <div className="catCirculoWrapper" onClick={() => onClick(cat.idCategoria)}>
            <div className={`catCirculoImg${activa ? ' catCirculoActiva' : ''}`}>
                {cat.imagen
                    ? <img src={cat.imagen} alt={cat.categoria} />
                    : <span className="catCirculoLetra">{cat.categoria[0].toUpperCase()}</span>
                }
            </div>
            <span className="catCirculoNombre">{cat.categoria}</span>
        </div>
    );
}

// ════════════════════════════════════════════════════════════
// HOOK: Cronómetro regresivo hasta una fecha límite
// ════════════════════════════════════════════════════════════
function useCronometro(hastaStr) {
    const calcular = () => {
        if (!hastaStr) return null;
        const diff = new Date(hastaStr) - new Date();
        if (diff <= 0) return { finalizado: true };
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        return { finalizado: false, d, h, m, s };
    };
    const [tiempo, setTiempo] = useState(calcular);
    useEffect(() => {
        if (!hastaStr) return;
        const id = setInterval(() => setTiempo(calcular()), 1000);
        return () => clearInterval(id);
    }, [hastaStr]);
    return tiempo;
}

// ════════════════════════════════════════════════════════════
// SUBCOMPONENTE: CardHorizontal
// ════════════════════════════════════════════════════════════
function CardHorizontal({ item, cupon = null, esOfertaDia = false, esRelampago = false }) {
    const { precioFinal, tienePrecioEspecial } = calcularPrecio(item);

    const precioOriginal = tienePrecioEspecial
        ? Number(item.precio)
        : Number(item.precioAnterior);

    // Badge de descuento: SOLO en ofertas del día (porcentaje) o relámpago
    const descuento = (esOfertaDia && item.tipoPrecioEspecial === 'porcentaje')
        ? calcularDescuento(Number(item.precio), precioFinal)
        : null;

    const agotado   = item.stock == 0 || item.stock === 'agotado';

    const esCuponPct  = cupon && cupon.tipo === 'porcentaje';
    const esCuponFijo = cupon && cupon.tipo === 'fijo';

    // Cronómetro: solo para cupones de porcentaje con fecha límite
    const cronoCupon = useCronometro(esCuponPct ? cupon.hasta : null);

    const [esFav, setEsFav] = useState(() => {
        const favs = JSON.parse(localStorage.getItem('favoritos')) || [];
        return favs.includes(item.idProducto);
    });

    return (
        <Anchor
            className='cardNueva'
            to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`}
            onClick={() => registrarVisto(item.idProducto)}
        >
            <div className='cardNueva-img'>
                {obtenerImagen(item)
                    ? <img src={obtenerImagen(item)} alt={item.titulo} />
                    : <div className='cardNueva-img-ph'>{item.titulo}</div>
                }
                {item.masVendido === 'si' && !agotado && (
                    <span className='badge-dest'>
                        <FontAwesomeIcon icon={faFire} style={{ fontSize: 10 }} /> DESTACADO
                    </span>
                )}
                {agotado && <span className='badge-agotado'>Agotado</span>}
                {!agotado && descuento > 0 && item.masVendido !== 'si' && (
                    <span className='badge-desc'>-{descuento}%</span>
                )}
                {!agotado && esCuponFijo && (
                    <span className='badge-cupon'>
                        <FontAwesomeIcon icon={faTag} style={{ fontSize: 9 }} /> Cupón
                    </span>
                )}
                {/* Cronómetro sobre imagen — solo cupones de porcentaje */}
                {esCuponPct && cronoCupon && (
                    cronoCupon.finalizado ? (
                        <div className='cupon-crono finalizado'>✅ Oferta finalizada</div>
                    ) : (
                        <div className='cupon-crono'>
                            ⏱ {cronoCupon.d > 0 && `${cronoCupon.d}d `}
                            {String(cronoCupon.h).padStart(2,'0')}:
                            {String(cronoCupon.m).padStart(2,'0')}:
                            {String(cronoCupon.s).padStart(2,'0')}
                        </div>
                    )
                )}
            </div>

            <div className='cardNueva-body'>
                <p className='cardNueva-titulo'>{item.titulo}</p>
                <div className='cardNueva-stars'>
                    <span className='stars-fill'>★★★★</span>
                    <span className='stars-empty'>★</span>
                </div>
                <div className='cardNueva-precios'>
                    <span className={`cardNueva-precio-final ${tienePrecioEspecial ? 'oferta' : ''}`}>
                        {fmt(precioFinal)}
                    </span>
                    {precioOriginal > 0 && precioOriginal > precioFinal && (
                        <span className='cardNueva-precio-orig'>{fmt(precioOriginal)}</span>
                    )}
                </div>
                {esCuponPct && (
                    <div className='cardNueva-cupon-precios'>
                        <span className='cupon-precio-orig'>{fmt(precioFinal)}</span>
                        <span className='cupon-precio-final'>{fmt(precioFinal - precioFinal * Number(cupon.descuento) / 100)}</span>
                        <span className='cupon-pct-label'>-{cupon.descuento}%</span>
                    </div>
                )}
                {esCuponFijo && cupon.hasta && (
                    <p className='cardNueva-cupon-validez'>
                        📅 Válido hasta: {new Date(cupon.hasta).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </p>
                )}
                {esCuponFijo && !cupon.hasta && (
                    <p className='cardNueva-cupon-hint'>🏷️ Aplicá un cupón en el carrito</p>
                )}
            </div>

            <div className='cardNueva-footer'>
                <button
                    className='btn-agregar-card'
                    disabled={agotado}
                    onClick={e => agregarAlCarrito(item, e)}
                >
                    <FontAwesomeIcon icon={faShoppingCart} style={{ fontSize: 13 }} />
                    {agotado ? 'Agotado' : 'Agregar'}
                </button>
                <button
                    className={`btn-fav-card ${esFav ? 'cardGrid-btnFavActivo' : ''}`}
                    onClick={e => {
                        const nuevoEstado = toggleFavorito(item.idProducto, e);
                        if (nuevoEstado !== null) setEsFav(nuevoEstado);
                    }}
                >
                    <FontAwesomeIcon icon={faHeart} style={{ fontSize: 13 }} />
                </button>
            </div>
        </Anchor>
    );
}

// ════════════════════════════════════════════════════════════
// SUBCOMPONENTE: CardGrid
// ════════════════════════════════════════════════════════════
function CardGrid({ item, cupon = null }) {
    const { precioFinal, tienePrecioEspecial } = calcularPrecio(item);
    const precioOriginal = tienePrecioEspecial
        ? Number(item.precio)
        : Number(item.precioAnterior);
    // En el grid de categorías NO se muestra badge de descuento
    const agotado   = item.stock == 0 || item.stock === 'agotado';

    const [esFav, setEsFav] = useState(() => {
        const favs = JSON.parse(localStorage.getItem('favoritos')) || [];
        return favs.includes(item.idProducto);
    });

    return (
        <Anchor
            className='cardGrid'
            to={`/producto/${item.idProducto}/${item.titulo.replace(/\s+/g, '-')}`}
            onClick={() => registrarVisto(item.idProducto)}
        >
            <div className='cardGrid-img'>
                {obtenerImagen(item)
                    ? <img src={obtenerImagen(item)} alt={item.titulo} />
                    : <div className='cardGrid-img-ph'>{item.titulo}</div>
                }
                {item.masVendido === 'si' && !agotado && (
                    <span className='badge-dest'>
                        <FontAwesomeIcon icon={faFire} style={{ fontSize: 10 }} /> DESTACADO
                    </span>
                )}
                {agotado && <span className='badge-agotado'>Agotado</span>}
                {!agotado && cupon && cupon.tipo === 'porcentaje' && (
                    <span className='badge-pct-cupon'>-{cupon.descuento}%</span>
                )}
                {!agotado && cupon && cupon.tipo === 'fijo' && (
                    <span className='badge-cupon'>
                        <FontAwesomeIcon icon={faTag} style={{ fontSize: 9 }} /> Cupón
                    </span>
                )}
            </div>

            <div className='cardGrid-body'>
                <p className='cardGrid-titulo'>{item.titulo}</p>
                <div className='cardNueva-stars'>
                    <span className='stars-fill'>★★★★</span>
                    <span className='stars-empty'>★</span>
                </div>
                <div className='cardNueva-precios'>
                    <span className={`cardNueva-precio-final ${tienePrecioEspecial ? 'oferta' : ''}`}>
                        {fmt(precioFinal)}
                    </span>
                    {precioOriginal > 0 && precioOriginal > precioFinal && (
                        <span className='cardNueva-precio-orig'>{fmt(precioOriginal)}</span>
                    )}
                </div>
                {cupon && cupon.tipo === 'porcentaje' && (
                    <div className='cardNueva-cupon-precios'>
                        <span className='cupon-precio-orig'>{fmt(precioFinal)}</span>
                        <span className='cupon-precio-final'>{fmt(precioFinal - precioFinal * Number(cupon.descuento) / 100)}</span>
                        <span className='cupon-pct-label'>-{cupon.descuento}%</span>
                    </div>
                )}
                {cupon && cupon.tipo === 'fijo' && (
                    <p className='cardNueva-cupon-hint'>🏷️ Aplicá un cupón en el carrito</p>
                )}
            </div>

            <div className='cardNueva-footer'>
                <button
                    className='btn-agregar-card'
                    disabled={agotado}
                    onClick={e => agregarAlCarrito(item, e)}
                >
                    <FontAwesomeIcon icon={faShoppingCart} style={{ fontSize: 13 }} />
                    {agotado ? 'Agotado' : 'Agregar'}
                </button>
                <button
                    className={`btn-fav-card ${esFav ? 'cardGrid-btnFavActivo' : ''}`}
                    onClick={e => {
                        const nuevoEstado = toggleFavorito(item.idProducto, e);
                        if (nuevoEstado !== null) setEsFav(nuevoEstado);
                    }}
                >
                    <FontAwesomeIcon icon={faHeart} style={{ fontSize: 13 }} />
                </button>
            </div>
        </Anchor>
    );
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL: Products
// ════════════════════════════════════════════════════════════
export default function Products() {

    // ── Estados ─────────────────────────────────────────────
    const [loadingProductos,         setLoadingProductos]         = useState(true);
    const [productos,                setProductos]                = useState([]);
    const [categorias,               setCategorias]               = useState([]);
    const [subcategorias,            setSubCategorias]            = useState([]);
    const [categoriaSeleccionada,    setCategoriaSeleccionada]    = useState('Todo');
    const [subcategoriaSeleccionada, setSubcategoriaSeleccionada] = useState('Todo');
    const [fixedCategories,          setFixedCategories]          = useState(false);
    const [recienVistos,             setRecienVistos]             = useState([]);
    const [codigos,                  setCodigos]                  = useState([]);

    const categoriasInputRef = useRef(null);

    // ── Handlers ────────────────────────────────────────────
    const handleClickCategoria = (cat) => {
        setCategoriaSeleccionada(cat);
        setSubcategoriaSeleccionada('Todo');
    };
    const handleClickSubcategoria = (sub) => setSubcategoriaSeleccionada(sub);

    const handleScroll = useCallback(() => {
        if (categoriasInputRef.current) {
            const banner  = document.querySelector('.BannerContain');
            const bannerH = banner ? banner.offsetHeight : 0;
            setFixedCategories(
                window.scrollY > categoriasInputRef.current.offsetTop &&
                window.scrollY > bannerH
            );
        }
    }, []);

    // ── Carga ────────────────────────────────────────────────
    useEffect(() => {
        setRecienVistos(JSON.parse(localStorage.getItem('recienVistos')) || []);

        fetch(`${baseURL}/categoriasGet.php`)
            .then(r => r.json())
            .then(data => setCategorias((data.categorias || []).sort((a, b) => a.orden - b.orden)))
            .catch(e => console.error('Error categorías:', e));

        fetch(`${baseURL}/subCategoriaGet.php`)
            .then(r => r.json())
            .then(data => setSubCategorias(data.subcategorias || []))
            .catch(e => console.error('Error subcategorías:', e));

        fetch(`${baseURL}/productosGet.php`)
            .then(r => r.json())
            .then(data => {
                setProductos(data.productos || []);
                setLoadingProductos(false);
            })
            .catch(e => { console.error('Error productos:', e); setLoadingProductos(false); });

        fetch(`${baseURL}/codigosGet.php`)
            .then(r => r.json())
            .then(data => setCodigos(data.codigos || []))
            .catch(e => console.error('Error códigos:', e));

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    // ── Derivados ────────────────────────────────────────────
    const productosVisibles = productos.filter(
        p => p.estadoProducto !== 'deshabilitado' && p.estadoProducto !== 'borrador'
    );

    const categoriasConProductos = categorias.filter(cat =>
        productosVisibles.some(p => p.idCategoria === cat.idCategoria)
    );

    // Secciones especiales (vista "Todo")
    const recienAgregados = [...productosVisibles]
        .sort((a, b) => Number(b.idProducto) - Number(a.idProducto))
        .slice(0, 20);

    const hoy = new Date();

    // ── Códigos activos ahora ────────────────────────────────
    const codigosActivos = codigos.filter(c => {
        const desde = c.desde ? new Date(c.desde) : null;
        const hasta = c.hasta ? new Date(c.hasta) : null;
        return (!desde || hoy >= desde) && (!hasta || hoy <= hasta);
    });

    // Devuelve el objeto cupón activo aplicable a un producto (o null si no tiene)
    const obtenerCuponProducto = (p) => {
        return codigosActivos.find(c => {
            if (c.idCategoria && String(c.idCategoria) === String(p.idCategoria)) return true;
            try {
                const prods = JSON.parse(c.productos || '[]');
                return prods.some(cp => String(cp.idProducto) === String(p.idProducto));
            } catch { return false; }
        }) || null;
    };

    // Productos con cupón de monto fijo activo → sección "Con cupón disponible"
    const productosConCuponFijo = productosVisibles.filter(p => {
        const c = obtenerCuponProducto(p);
        return c && c.tipo === 'fijo';
    });

    // Productos con cupón de porcentaje activo → se suman a "Ofertas relámpago"
    const productosConCuponPct = productosVisibles.filter(p => {
        const c = obtenerCuponProducto(p);
        return c && c.tipo === 'porcentaje';
    });

    // Mantener productosConCupon como unión (para badges en otras secciones)
    const productosConCupon = productosVisibles.filter(p => obtenerCuponProducto(p) !== null);

    const ofertasDelDia = productosVisibles.filter(p => {
        if (!p.precioEspecial) return false;
        const desde = p.precioEspecialInicio ? new Date(p.precioEspecialInicio) : null;
        const hasta = p.precioEspecialFin    ? new Date(p.precioEspecialFin)    : null;
        return (!desde || hoy >= desde) && (!hasta || hoy <= hasta);
    });

    // ── NUEVO SESIÓN 5: Ofertas relámpago ───────────────────
    // Precio especial activo con 5 días o menos para vencer
    const ofertasRelampago = productosVisibles.filter(p => {
        if (!p.precioEspecial) return false;
        const hasta = p.precioEspecialFin ? new Date(p.precioEspecialFin) : null;
        const desde = p.precioEspecialInicio ? new Date(p.precioEspecialInicio) : null;
        const diasRestantes = hasta
            ? Math.ceil((hasta - hoy) / (1000 * 60 * 60 * 24))
            : null;
        const dentroDeRango = (!desde || hoy >= desde) && (!hasta || hoy <= hasta);
        return dentroDeRango && diasRestantes !== null && diasRestantes <= 5;
    });

    const productosRecienVistos = recienVistos
        .map(id => productosVisibles.find(p => p.idProducto === id))
        .filter(Boolean)
        .slice(0, 20);

    const subcatsActivas = subcategorias.filter(s =>
        s.idCategoria === categoriaSeleccionada &&
        productosVisibles.some(p => p.idSubCategoria === s.idSubCategoria)
    );

    const productosGrid = productosVisibles.filter(item => {
        if (!subcategoriaSeleccionada || subcategoriaSeleccionada === 'Todo')
            return item.idCategoria === categoriaSeleccionada;
        return item.idSubCategoria === subcategoriaSeleccionada;
    });

    // ════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════
    return (
        <div className='ProductsContain'>
            <ToastContainer />

            {/* ── Barra de categorías ── */}
            {productosVisibles.length > 0 && (
                <div
                    className={`categoriasInputs ${fixedCategories ? 'fixed' : ''}`}
                    id='FlexIn'
                    ref={categoriasInputRef}
                >
                    {/* ── NUEVO SESIÓN 5: Círculos de categorías ── */}
                    <div className='catCirculosContainer'>

                        {/* Círculo "Todo" */}
                        <div
                            className="catCirculoWrapper"
                            onClick={() => handleClickCategoria('Todo')}
                        >
                            <div className={`catCirculoImg${categoriaSeleccionada === 'Todo' ? ' catCirculoActiva' : ''}`}>
                                <span className="catCirculoLetra">★</span>
                            </div>
                            <span className="catCirculoNombre">Todo</span>
                        </div>

                        {/* Resto de categorías */}
                        {categoriasConProductos.map(cat => (
                            <CatCirculo
                                key={cat.idCategoria}
                                cat={cat}
                                activa={categoriaSeleccionada === cat.idCategoria}
                                onClick={handleClickCategoria}
                            />
                        ))}
                    </div>

                    {/* Subcategorías — solo si la categoría activa tiene subcats con productos */}
                    {categoriaSeleccionada !== 'Todo' && subcatsActivas.length > 0 && (
                        <div className='categoriasInputsFilter' id='subcategoriasInputs'>
                            <input type='button' value='Todo'
                                onClick={() => handleClickSubcategoria('Todo')}
                                id='subcategoriaInput'
                                style={{
                                    borderBottom: (!subcategoriaSeleccionada || subcategoriaSeleccionada === 'Todo') ? '2px solid #023992' : '',
                                    color:        (!subcategoriaSeleccionada || subcategoriaSeleccionada === 'Todo') ? '#023992' : '',
                                }}
                            />
                            {subcatsActivas.map(s => (
                                <div key={s.idSubCategoria}>
                                    <input type='button' value={s.subcategoria}
                                        id='subcategoriaInput'
                                        onClick={() => handleClickSubcategoria(s.idSubCategoria)}
                                        style={{
                                            borderBottom: subcategoriaSeleccionada === s.idSubCategoria ? '2px solid #023992' : '',
                                            color:        subcategoriaSeleccionada === s.idSubCategoria ? '#023992' : '',
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ── Contenido principal ── */}
            {loadingProductos ? (
                <ProductosLoading />
            ) : (
                <div className='Products'>

                    {/* ── Vista "Todo": secciones especiales ── */}
                    {categoriaSeleccionada === 'Todo' && (
                        <>
                            {/* Más vendidos */}
                            {productosVisibles.some(i => i.masVendido === 'si') && (
                                <div className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3>
                                            <FontAwesomeIcon icon={faFire} style={{ color: '#f59e0b', marginRight: 6, fontSize: 14 }} />
                                            Más vendidos
                                        </h3>
                                    </div>
                                    <Swiper grabCursor slidesPerView='auto' id='swiper_container_products' autoplay={{ delay: 3000 }}>
                                        {productosVisibles.filter(i => i.masVendido === 'si').slice(0, 20).map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products'>
                                                <CardHorizontal item={item} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {/* Recién agregados */}
                            {recienAgregados.length > 0 && (
                                <div className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3>
                                            <FontAwesomeIcon icon={faClock} style={{ color: '#6366f1', marginRight: 6, fontSize: 14 }} />
                                            Recién agregados
                                        </h3>
                                    </div>
                                    <Swiper grabCursor slidesPerView='auto' id='swiper_container_products'>
                                        {recienAgregados.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products'>
                                                <CardHorizontal item={item} cupon={obtenerCuponProducto(item)} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {/* Ofertas del día */}
                            {ofertasDelDia.length > 0 && (
                                <div className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3>
                                            <FontAwesomeIcon icon={faBolt} style={{ color: '#ef4444', marginRight: 6, fontSize: 14 }} />
                                            Ofertas del día
                                        </h3>
                                    </div>
                                    <Swiper grabCursor slidesPerView='auto' id='swiper_container_products' autoplay={{ delay: 2500 }}>
                                        {ofertasDelDia.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products'>
                                                <CardHorizontal item={item} cupon={obtenerCuponProducto(item)} esOfertaDia={true} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {/* ── Ofertas relámpago + cupones de porcentaje ── */}
                            {ofertasRelampago.length > 0 && (
                                <div className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3 className='tituloRelampago'>
                                            <span className='relampagoIcono'>⚡</span>
                                            Ofertas relámpago
                                        </h3>
                                    </div>
                                    <Swiper grabCursor slidesPerView='auto' id='swiper_container_products' autoplay={{ delay: 2000 }}>
                                        {ofertasRelampago.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products'>
                                                <CardHorizontal item={item} cupon={obtenerCuponProducto(item)} esRelampago={true} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {/* Con cupón disponible — solo monto fijo */}
                            {productosConCuponFijo.length > 0 && (
                                <div className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3>
                                            <FontAwesomeIcon icon={faTag} style={{ color: '#8b5cf6', marginRight: 6, fontSize: 14 }} />
                                            Con cupón disponible
                                        </h3>
                                    </div>
                                    <Swiper grabCursor slidesPerView='auto' id='swiper_container_products' autoplay={{ delay: 3500 }}>
                                        {productosConCuponFijo.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products'>
                                                <CardHorizontal item={item} cupon={obtenerCuponProducto(item)} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}

                            {/* Recién vistos */}
                            {productosRecienVistos.length > 0 && (
                                <div className='categoriSection'>
                                    <div className='deFlexTitlesection'>
                                        <h3>
                                            <FontAwesomeIcon icon={faEye} style={{ color: '#0ea5e9', marginRight: 6, fontSize: 14 }} />
                                            Recién vistos
                                        </h3>
                                    </div>
                                    <Swiper grabCursor slidesPerView='auto' id='swiper_container_products'>
                                        {productosRecienVistos.map(item => (
                                            <SwiperSlide key={item.idProducto} id='SwiperSlide-scroll-products'>
                                                <CardHorizontal item={item} cupon={obtenerCuponProducto(item)} />
                                            </SwiperSlide>
                                        ))}
                                    </Swiper>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── Vista categoría seleccionada: grid ── */}
                    {categoriaSeleccionada !== 'Todo' && (
                        <div className='gridProductos'>
                            {productosGrid.map(item => (
                                <CardGrid key={item.idProducto} item={item} cupon={obtenerCuponProducto(item)} />
                            ))}
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}